import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { detectAIConfiguration, testAIConnection, getAIStatusMessage } from '../lib/aiDetection';

/**
 * 🎯 AI Hub Context - Centre de contrôle IA Komby
 * Architecture "Multiprise" pour connecter toutes les fonctionnalités IA
 */

interface AIPlugin {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  category: 'support' | 'onboarding' | 'crm' | 'content' | 'analytics' | 'other';
  confidenceThreshold: number;
  lastUsed?: Date;
  usageCount: number;
}

interface AIHubState {
  // Configuration
  isConfigured: boolean;
  isOnline: boolean;
  lastConnectionTest?: Date;
  
  // Plugins actifs
  plugins: AIPlugin[];
  activePlugins: string[];
  
  // Statistiques
  totalRequests: number;
  successfulRequests: number;
  averageResponseTime: number;
  
  // Status
  statusMessage: string;
  statusType: 'success' | 'warning' | 'error';
  statusIcon: string;
}

interface AIHubContextType extends AIHubState {
  // Actions
  registerPlugin: (plugin: Omit<AIPlugin, 'usageCount'>) => void;
  unregisterPlugin: (pluginId: string) => void;
  activatePlugin: (pluginId: string) => void;
  deactivatePlugin: (pluginId: string) => void;
  testConnection: () => Promise<boolean>;
  
  // IA Generator Methods
  generateResponse: (prompt: string, context: string, pluginId: string) => Promise<string>;
  getPluginByCategory: (category: AIPlugin['category']) => AIPlugin[];
  
  // Utils
  refreshConfiguration: () => void;
}

const AIHubContext = createContext<AIHubContextType | undefined>(undefined);

export const useAIHub = () => {
  const context = useContext(AIHubContext);
  if (!context) {
    throw new Error('useAIHub must be used within AIHubProvider');
  }
  return context;
};

interface AIHubProviderProps {
  children: ReactNode;
}

export const AIHubProvider: React.FC<AIHubProviderProps> = ({ children }) => {
  const [state, setState] = useState<AIHubState>({
    isConfigured: false,
    isOnline: false,
    plugins: [],
    activePlugins: [],
    totalRequests: 0,
    successfulRequests: 0,
    averageResponseTime: 0,
    statusMessage: 'Configuration en cours...',
    statusType: 'warning',
    statusIcon: '⚙️'
  });

  // 🔍 Initialisation et détection de configuration
  useEffect(() => {
    initializeAIHub();
  }, []);

  const initializeAIHub = async () => {
    const config = detectAIConfiguration();
    const status = getAIStatusMessage(config);
    
    setState(prev => ({
      ...prev,
      isConfigured: config.isConfigured,
      statusMessage: status.message,
      statusType: status.type,
      statusIcon: status.icon
    }));

    // Test de connexion si configuré
    if (config.isConfigured) {
      const connectionTest = await testAIConnection();
      setState(prev => ({
        ...prev,
        isOnline: connectionTest.success,
        lastConnectionTest: new Date()
      }));
    }

    // Enregistrer les plugins par défaut
    registerDefaultPlugins();
  };

  const registerDefaultPlugins = () => {
    const defaultPlugins: Omit<AIPlugin, 'usageCount'>[] = [
      {
        id: 'support-assistant',
        name: '🎫 Support Assistant',
        description: 'Réponses automatiques aux tickets de support',
        isActive: true,
        category: 'support',
        confidenceThreshold: 0.8
      },
      {
        id: 'onboarding-helper',
        name: '🚀 Onboarding Helper',
        description: 'Assistance intelligente pour l\'onboarding utilisateur',
        isActive: false,
        category: 'onboarding',
        confidenceThreshold: 0.7
      },
      {
        id: 'content-generator',
        name: '✍️ Content Generator',
        description: 'Génération de contenu marketing et communication',
        isActive: false,
        category: 'content',
        confidenceThreshold: 0.6
      },
      {
        id: 'crm-analyzer',
        name: '📊 CRM Analyzer',
        description: 'Analyse intelligente des données clients',
        isActive: false,
        category: 'crm',
        confidenceThreshold: 0.75
      }
    ];

    defaultPlugins.forEach(plugin => registerPlugin(plugin));
  };

  // 🔌 Gestion des plugins (Architecture Multiprise)
  const registerPlugin = (plugin: Omit<AIPlugin, 'usageCount'>) => {
    setState(prev => ({
      ...prev,
      plugins: [
        ...prev.plugins.filter(p => p.id !== plugin.id),
        { ...plugin, usageCount: 0 }
      ],
      activePlugins: plugin.isActive 
        ? [...prev.activePlugins.filter(id => id !== plugin.id), plugin.id]
        : prev.activePlugins.filter(id => id !== plugin.id)
    }));
  };

  const unregisterPlugin = (pluginId: string) => {
    setState(prev => ({
      ...prev,
      plugins: prev.plugins.filter(p => p.id !== pluginId),
      activePlugins: prev.activePlugins.filter(id => id !== pluginId)
    }));
  };

  const activatePlugin = (pluginId: string) => {
    setState(prev => ({
      ...prev,
      plugins: prev.plugins.map(p => 
        p.id === pluginId ? { ...p, isActive: true } : p
      ),
      activePlugins: [...prev.activePlugins.filter(id => id !== pluginId), pluginId]
    }));
  };

  const deactivatePlugin = (pluginId: string) => {
    setState(prev => ({
      ...prev,
      plugins: prev.plugins.map(p => 
        p.id === pluginId ? { ...p, isActive: false } : p
      ),
      activePlugins: prev.activePlugins.filter(id => id !== pluginId)
    }));
  };

  // 🤖 Générateur IA Central
  const generateResponse = async (
    prompt: string, 
    context: string, 
    pluginId: string
  ): Promise<string> => {
    if (!state.isConfigured || !state.isOnline) {
      throw new Error('AI Hub not configured or offline');
    }

    const plugin = state.plugins.find(p => p.id === pluginId);
    if (!plugin || !plugin.isActive) {
      throw new Error(`Plugin ${pluginId} not found or inactive`);
    }

    const startTime = Date.now();

    try {
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_OPENAI_API_KEY}`
        },
        body: JSON.stringify({
          model: import.meta.env.VITE_OPENAI_MODEL || 'gpt-4o-mini',
          messages: [
            {
              role: 'system',
              content: `Tu es ${plugin.name} pour Komby. ${plugin.description}. Contexte: ${context}`
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          max_tokens: 800,
          temperature: 0.7
        })
      });

      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const aiResponse = data.choices[0]?.message?.content || 'Désolé, je ne peux pas répondre.';

      // Mise à jour des statistiques
      setState(prev => ({
        ...prev,
        totalRequests: prev.totalRequests + 1,
        successfulRequests: prev.successfulRequests + 1,
        averageResponseTime: (prev.averageResponseTime + responseTime) / 2,
        plugins: prev.plugins.map(p => 
          p.id === pluginId 
            ? { ...p, usageCount: p.usageCount + 1, lastUsed: new Date() }
            : p
        )
      }));

      return aiResponse;
    } catch (error) {
      // Mise à jour des statistiques d'erreur
      setState(prev => ({
        ...prev,
        totalRequests: prev.totalRequests + 1
      }));

      throw error;
    }
  };

  // 🔍 Utilitaires
  const getPluginByCategory = (category: AIPlugin['category']) => {
    return state.plugins.filter(p => p.category === category);
  };

  const testConnection = async (): Promise<boolean> => {
    const result = await testAIConnection();
    setState(prev => ({
      ...prev,
      isOnline: result.success,
      lastConnectionTest: new Date()
    }));
    return result.success;
  };

  const refreshConfiguration = () => {
    initializeAIHub();
  };

  const value: AIHubContextType = {
    ...state,
    registerPlugin,
    unregisterPlugin,
    activatePlugin,
    deactivatePlugin,
    testConnection,
    generateResponse,
    getPluginByCategory,
    refreshConfiguration
  };

  return (
    <AIHubContext.Provider value={value}>
      {children}
    </AIHubContext.Provider>
  );
}; 